import { animated, useSpring } from "@react-spring/web";
import { Button, Flex, Layout, Tooltip } from "antd";
import { CompanyMenu } from "app/CompanyMenu";
import { Logo } from "components/Logo";
import { useReducedMotion } from "hooks/useReducedMotion";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { AppMenu } from "./AppMenu";
import { HireExpertCard } from "./HireExpertCard";
import { useSidebar } from "./SidebarContext";
import { SidebarUserButton } from "./SidebarUserButton";
import { SIDER_RAIL_WIDTH, SIDER_WIDTH } from "./siderLayout";

/**
 * The left navigation (EQU-1120).
 *
 * Four stacked pieces, top to bottom: the wordmark, the tinted company panel
 * (`CompanyMenu`), the workspace rows (`AppMenu`), and a bottom block pushed to
 * the foot of the column — the Hire an Expert card, the user button and the
 * collapse arrow.
 *
 * The bottom block sits OUTSIDE the scrolling region on purpose. The nav is
 * taller than a laptop viewport, so inside it the user button and the collapse
 * arrow would be below the fold on exactly the screens where the sidebar is
 * most in the way. See the `.app-sider-nav` note in styles/index.css for why
 * that region scrolls at all when the ticket says "no scrollbars".
 */
export function AppSider() {
	const { collapsed, setCollapsed } = useSidebar();
	const reduced = useReducedMotion();
	/**
	 * A responsive OVERRIDE, deliberately separate from the stored preference.
	 *
	 * 🪤 `setCollapsed` writes to localStorage. Driving it from `onBreakpoint`
	 * would make one visit on a phone silently collapse the user's desktop
	 * sidebar on their next session. The breakpoint forces the rail; it never
	 * records an opinion.
	 */
	const [narrow, setNarrow] = useState(false);
	const railed = collapsed || narrow;

	const logoSpring = useSpring({
		opacity: 1,
		transform: railed ? "scale(0.9)" : "scale(1)",
		config: { tension: 200, friction: 20 },
		immediate: reduced,
		reset: true,
	});

	return (
		// 🚨 Without a breakpoint this Sider held a FIXED width at every viewport,
		// so `<main>` measured 140px at a 390px viewport on EVERY route — content
		// crushed, not scrolled, with every word wrapping to a character or two.
		// Measured before: main 140px. After: 310px.
		<Layout.Sider
			className="app-sider"
			width={SIDER_WIDTH}
			collapsedWidth={SIDER_RAIL_WIDTH}
			collapsible
			breakpoint="lg"
			onBreakpoint={setNarrow}
			collapsed={railed}
			trigger={null}
			style={siderStyle}
		>
			<Flex vertical style={wrapperStyle}>
				<Flex
					style={logoBlockStyle}
					align="center"
					justify={railed ? "center" : "flex-start"}
				>
					<animated.div style={logoSpring}>
						{railed ? (
							<Logo width={26} small />
						) : (
							<Logo width={100} key="full_logo" />
						)}
					</animated.div>
				</Flex>
				<div className="app-sider-nav">
					<CompanyMenu />
					<AppMenu />
				</div>
				<div className="app-sider-bottom">
					<HireExpertCard railed={railed} />
					<SidebarUserButton railed={railed} />
					<div className="sider-collapse-row">
						<Tooltip
							title={railed ? "Click to expand sidebar" : undefined}
							placement="right"
							trigger={["hover", "focus"]}
						>
							<Button
								type="text"
								className={`sider-collapse-button${
									railed ? " sider-collapse-button--railed" : ""
								}`}
								icon={
									<ArrowLeft
										size={18}
										strokeWidth={1.8}
										className="sider-collapse-icon"
									/>
								}
								onClick={() => setCollapsed(!railed)}
								aria-label={railed ? "Expand sidebar" : "Collapse sidebar"}
								aria-expanded={!railed}
							/>
						</Tooltip>
					</div>
				</div>
			</Flex>
		</Layout.Sider>
	);
}

const siderStyle: React.CSSProperties = {
	background: "var(--color-surface)",
	borderRight: "1px solid var(--sidebar-border)",
	// Matches the content pane: the support-access bar sits above both, and this
	// is the only other 100vh pane. Defaults to 0px when no session is active.
	height: "calc(100vh - var(--support-access-bar-height, 0px))",
	position: "sticky",
	top: 0,
	// The sidebar never scrolls sideways (EQU-1120 §6). The vertical scroll
	// belongs to `.app-sider-nav` inside it, not to this box.
	overflow: "hidden",
};

const wrapperStyle: React.CSSProperties = {
	height: "100%",
	padding: "14px 12px 10px",
};

const logoBlockStyle: React.CSSProperties = {
	flexShrink: 0,
	height: 36,
	overflow: "hidden",
};
